import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '@clerk/clerk-react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import BlockIcon from '@mui/icons-material/Block';
import SpeedIcon from '@mui/icons-material/Speed';
import { API_BASE_URL } from '../types';

interface HealthMetrics {
  is_healthy: boolean;
  is_blocked: boolean;
  blocked_until: string | null;
  total_requests: number;
  total_successes: number;
  total_failures: number;
  success_rate: number;
  recent_success_rate: number;
  consecutive_failures: number;
  avg_response_time: number | null;
  error_counts: {
    '403_forbidden': number;
    '429_rate_limit': number;
    '503_unavailable': number;
    'timeout': number;
    'login_failure': number;
    'csrf_failure': number;
    'other': number;
  };
  last_health_check: string;
  quote_stats: {
    total_quotes_found: number;
    total_no_quotes_found: number;
    quotes_found_rate: number;
    no_quotes_rate: number;
    recent_quotes_found: number;
    recent_no_quotes: number;
  };
}

interface StatusResponse {
  status: 'operational' | 'degraded' | 'blocked' | 'unknown';
  service: string;
  message: string;
  health: HealthMetrics;
}

const StatusMonitor: React.FC = () => {
  const { getToken } = useAuth();
  const [statusData, setStatusData] = useState<StatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchStatus = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getToken({ template: 'covervision-backend' });

      const response = await axios.get<StatusResponse>(
        `${API_BASE_URL}/buyback/status`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setStatusData(response.data);
      setLastUpdated(new Date());
    } catch (err: any) {
      console.error('Error fetching status:', err);
      setError(err.response?.data?.detail || 'Failed to fetch status');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return 'success';
      case 'degraded':
        return 'warning';
      case 'blocked':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational':
        return <CheckCircleIcon />;
      case 'degraded':
        return <WarningIcon />;
      case 'blocked':
        return <BlockIcon />;
      default:
        return null;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          ThriftBooks Service Monitor
        </Typography>
        <Button
          variant="contained"
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
          onClick={fetchStatus}
          disabled={isLoading}
        >
          Refresh Status
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {lastUpdated && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Last updated: {lastUpdated.toLocaleString()}
        </Typography>
      )}

      {!statusData && !isLoading && !error && (
        <Alert severity="info">
          Click "Refresh Status" to check service health
        </Alert>
      )}

      {statusData && (
        <Grid container spacing={3}>
          {/* Overall Status Card */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ mr: 2 }}>
                    {getStatusIcon(statusData.status)}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" component="div">
                      Service Status
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {statusData.message}
                    </Typography>
                  </Box>
                  <Chip
                    label={statusData.status.toUpperCase()}
                    color={getStatusColor(statusData.status) as any}
                    sx={{ fontSize: '1rem', padding: '8px' }}
                  />
                </Box>

                {statusData.health.is_blocked && statusData.health.blocked_until && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    <strong>Service Blocked</strong>
                    <br />
                    Automatically resuming at: {new Date(statusData.health.blocked_until).toLocaleString()}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Key Metrics Cards */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Success Rate
                </Typography>
                <Typography variant="h4" component="div" color={
                  statusData.health.success_rate >= 90 ? 'success.main' :
                  statusData.health.success_rate >= 70 ? 'warning.main' : 'error.main'
                }>
                  {statusData.health.success_rate.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Recent: {statusData.health.recent_success_rate.toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Requests
                </Typography>
                <Typography variant="h4" component="div">
                  {statusData.health.total_requests}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ✓ {statusData.health.total_successes} | ✗ {statusData.health.total_failures}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <SpeedIcon sx={{ mr: 0.5, fontSize: 16 }} />
                  Avg Response Time
                </Typography>
                <Typography variant="h4" component="div">
                  {statusData.health.avg_response_time
                    ? `${statusData.health.avg_response_time.toFixed(2)}s`
                    : 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {statusData.health.consecutive_failures > 0
                    ? `${statusData.health.consecutive_failures} consecutive failures`
                    : 'No recent failures'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Quote Statistics */}
          {statusData.health.quote_stats && (
            <>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Quote Statistics
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2}>
                      {/* Overall Quote Rate */}
                      <Grid item xs={12} md={6}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography color="text.secondary" gutterBottom>
                              Overall Quote Success Rate
                            </Typography>
                            <Typography variant="h3" component="div" color={
                              statusData.health.quote_stats.quotes_found_rate >= 70 ? 'success.main' :
                              statusData.health.quote_stats.quotes_found_rate >= 40 ? 'warning.main' : 'error.main'
                            }>
                              {statusData.health.quote_stats.quotes_found_rate.toFixed(1)}%
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {statusData.health.quote_stats.total_quotes_found} quotes found / {statusData.health.quote_stats.total_no_quotes_found} no quotes (account at limit)
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Recent Quote Activity */}
                      <Grid item xs={12} md={6}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography color="text.secondary" gutterBottom>
                              Recent Activity (Last 20 Requests)
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h4" component="div" color="success.main">
                                  {statusData.health.quote_stats.recent_quotes_found}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  With Quotes
                                </Typography>
                              </Box>
                              <Divider orientation="vertical" flexItem />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h4" component="div" color="warning.main">
                                  {statusData.health.quote_stats.recent_no_quotes}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  No Quotes (At Limit)
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Interpretation Alert */}
                      {statusData.health.quote_stats.quotes_found_rate < 50 && (
                        <Grid item xs={12}>
                          <Alert severity="warning">
                            <strong>Low Quote Rate Detected</strong>
                            <br />
                            Most accounts are at their daily limit (2000 scans). Consider adding more accounts or wait for midnight UTC reset.
                          </Alert>
                        </Grid>
                      )}

                      {statusData.health.quote_stats.recent_no_quotes > 15 && (
                        <Grid item xs={12}>
                          <Alert severity="info">
                            Recent requests are mostly hitting account limits. Multi-account rotation is actively working to find available accounts.
                          </Alert>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}

          {/* Error Breakdown */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Error Breakdown
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>403 Forbidden (WAF/Auth)</strong></TableCell>
                        <TableCell align="right">
                          <Chip
                            label={statusData.health.error_counts['403_forbidden']}
                            color={statusData.health.error_counts['403_forbidden'] > 0 ? 'error' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>429 Rate Limit</strong></TableCell>
                        <TableCell align="right">
                          <Chip
                            label={statusData.health.error_counts['429_rate_limit']}
                            color={statusData.health.error_counts['429_rate_limit'] > 0 ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>503 Service Unavailable</strong></TableCell>
                        <TableCell align="right">
                          <Chip
                            label={statusData.health.error_counts['503_unavailable']}
                            color={statusData.health.error_counts['503_unavailable'] > 0 ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Timeouts</strong></TableCell>
                        <TableCell align="right">
                          <Chip
                            label={statusData.health.error_counts['timeout']}
                            color={statusData.health.error_counts['timeout'] > 0 ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Login Failures</strong></TableCell>
                        <TableCell align="right">
                          <Chip
                            label={statusData.health.error_counts['login_failure']}
                            color={statusData.health.error_counts['login_failure'] > 0 ? 'error' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>CSRF Token Failures</strong></TableCell>
                        <TableCell align="right">
                          <Chip
                            label={statusData.health.error_counts['csrf_failure']}
                            color={statusData.health.error_counts['csrf_failure'] > 0 ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Other Errors</strong></TableCell>
                        <TableCell align="right">
                          <Chip
                            label={statusData.health.error_counts['other']}
                            color={statusData.health.error_counts['other'] > 0 ? 'default' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Health Status Details */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Health Status Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Healthy
                    </Typography>
                    <Typography variant="body1">
                      {statusData.health.is_healthy ? '✓ Yes' : '✗ No'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Blocked
                    </Typography>
                    <Typography variant="body1">
                      {statusData.health.is_blocked ? '✗ Yes' : '✓ No'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Consecutive Failures
                    </Typography>
                    <Typography variant="body1">
                      {statusData.health.consecutive_failures}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Last Health Check
                    </Typography>
                    <Typography variant="body1">
                      {new Date(statusData.health.last_health_check).toLocaleTimeString()}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default StatusMonitor;
